// SkipUnitNumberInput — text field with auto-suggest of the next unit
// number for a given stock-type prefix. Computed client-side from the
// cached inventory list (no extra round-trip).
//
// Suggestion logic:
//   prefix = stockTypePrefix prop (e.g. SKP, BIN, ROR, CRU)
//   next = max(parseInt(skip_number.slice(prefix.length))) + 1, padded to 3
//   default starting suggestion = "<PREFIX>001" when no rows match.

import { useMemo } from 'react';
import { useSkipInventory } from '../../hooks/useSkipInventory';
import styles from './SkipUnitNumberInput.module.css';

export interface SkipUnitNumberInputProps {
  /** Manually-set prefix from the chosen stock type, e.g. "SKP". null
   *  before the user picks a stock type. */
  stockTypePrefix: string | null;
  value: string;
  onChange: (next: string) => void;
}

function suggestNext(prefix: string, existing: string[]): string {
  let max = 0;
  for (const s of existing) {
    if (!s.startsWith(prefix)) continue;
    const tail = s.slice(prefix.length);
    const n = parseInt(tail, 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return `${prefix}${String(max + 1).padStart(3, '0')}`;
}

export function SkipUnitNumberInput({
  stockTypePrefix,
  value,
  onChange,
}: SkipUnitNumberInputProps): JSX.Element {
  const inventoryQuery = useSkipInventory({ limit: 100 });

  const suggestion = useMemo(() => {
    if (!stockTypePrefix) return null;
    const numbers = (inventoryQuery.data?.inventory ?? []).map((i) => i.skip_number);
    return suggestNext(stockTypePrefix, numbers);
  }, [stockTypePrefix, inventoryQuery.data]);

  return (
    <div className={styles.wrap}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={suggestion ?? 'Select stock type first'}
        className={styles.input}
        aria-label="Unit Number"
      />
      {suggestion && suggestion !== value ? (
        <button
          type="button"
          className={styles.suggestion}
          onClick={() => onChange(suggestion)}
        >
          Use {suggestion}
        </button>
      ) : null}
    </div>
  );
}
